import {
    CloseOutlined,
    FilterAltOffOutlined,
    FilterAltOutlined,
    Refresh,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    Grid,
    IconButton,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { FormEvent, useEffect, useRef, useState } from "react";
import { MultiPermissionAuthorize } from "../../Utilities/MultiPermissionAuthorize";
import { Print } from "../../Utilities/Print";
import { TableToExcel } from "../../Utilities/TableToExcel";
import UpdatedSearchableInput from "../../Utilities/UpdatedSearchableInput";
import { Header } from "../Header/Header";
import useGetRecoveryPlan from "./useGetRecoveryPlan";

const initialFilter = {
    district: "",
    village: [] as string[],
    hamlet: [] as string[],
    powerstation: "",
    startDate: "",
    endDate: "",
    minMonths: 1,
};

export default function RecoveryPlan() {
    const [pagination, setPagination] = useState({
        page: 0,
        limit: 5000000000,
    });
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM

    const theme = useTheme();
    const [filter, setFilter] = useState(initialFilter);
    const [billingMonth, setBillingMonth] = useState("");
    const [year, month] = billingMonth.split("-") ?? ["", ""];

    const [printMode, setPrintMode] = useState(false);
    const [refetchCount, setRefetchCount] = useState(0);
    const printRef = useRef<HTMLTableElement>(null);
    const [clearAll, setClearAll] = useState({
        district: false,
        village: false,
        powerstation: false,
        search: false,
        connectionType: false,
        billingMonth: false,
    });

    const { data, isLoading, consumerTypes } = useGetRecoveryPlan({
        ...pagination,
        page: pagination.page + 1,
        district: filter.district,
        village: filter.village,
        hamlet: filter.hamlet,
        powerstation: filter.powerstation,
        startDate: filter.startDate,
        endDate: filter.endDate,
        minMonths: filter.minMonths ? filter.minMonths : 1,
        month: month as any,
        year: year as any,
        deps: [refetchCount],
    });

    const grandTotals: Record<string, number> = {};
    consumerTypes.forEach((ct) => (grandTotals[ct] = 0));

    let totalConsumers = 0;
    let totalDefaulters = 0;

    data.forEach((row) => {
        row.hamlet.forEach((hamlet) => {
            consumerTypes.forEach((ct) => {
                const val = hamlet.countOfConsumers?.[ct] || 0;
                grandTotals[ct] += val;
                totalConsumers += val;
            });

            totalDefaulters += hamlet.countOfDefaulters || 0;
        });
    });
    function handleApplyFilter(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        setRefetchCount((f) => f + 1);
    }

    useEffect(() => {
        if (filter.district === "all") {
            setRefetchCount(0);
        }
    }, [filter.district]);

    /* ================= CLEAR ALL ================= */

    const handleClearAll = () => {
        // reset UI filters

        // reset applied filters
        setFilter(initialFilter);
        setBillingMonth("");
        // notify child inputs
        setClearAll({
            district: true,
            village: true,
            powerstation: true,
            search: true,
            connectionType: true,
            billingMonth: true,
        });

        // reset search trigger & pagination

        // turn flags off after render
        setTimeout(
            () =>
                setClearAll({
                    district: false,
                    village: false,
                    powerstation: false,
                    search: false,
                    connectionType: false,
                    billingMonth: false,
                }),
            0
        );
    };

    return (
        <div>
            <Card
                elevation={0}
                sx={(theme) => ({
                    bgcolor: theme.palette.common.white,
                    p: 2,
                    mb: "1rem",
                })}
            >
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1.5}
                    mb={1.5}
                >
                    <Grid item xs={12}>
                        <Typography variant="h5" color="GrayText">
                            Recovery Plan
                        </Typography>
                    </Grid>
                </Grid>

                <Grid
                    container
                    spacing={1.5}
                    component="form"
                    onSubmit={handleApplyFilter}
                    mb={2}
                >
                    <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                        <Grid item xs={12} md={6} lg={3}>
                            <UpdatedSearchableInput
                                label="District"
                                api="/region/district"
                                filter={filter}
                                setFilter={setFilter}
                                clearAll={
                                    !filter.district ||
                                    filter.district === "all"
                                }
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ POWERSTATION"]}>
                        <Grid item xs={12} md={6} lg={3}>
                            <UpdatedSearchableInput
                                label="Powerstation"
                                api="/powerstation"
                                filter={filter}
                                setFilter={setFilter}
                                dep={filter.district}
                                params={{
                                    district: filter.district,
                                }}
                                clearAll={
                                    !filter.district ||
                                    filter.district === "all"
                                }
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ VILLAGE"]}>
                        <Grid item xs={12} md={6} lg={3}>
                            <UpdatedSearchableInput
                                label="Village"
                                api="/region/village"
                                filter={filter}
                                setFilter={setFilter}
                                dep={[filter.district, filter.powerstation]}
                                params={{
                                    district: filter.district,
                                    powerstation: filter.powerstation,
                                }}
                                clearAll={
                                    !filter.district ||
                                    filter.district === "all"
                                }
                                multiple
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ HAMLET"]}>
                        <Grid item xs={12} md={6} lg={3}>
                            <UpdatedSearchableInput
                                label="Hamlet"
                                api="/region/hamlet"
                                filter={filter}
                                setFilter={setFilter}
                                dep={[filter.village, filter.powerstation]}
                                params={{
                                    village: filter.village,
                                    powerstation: filter.powerstation,
                                }}
                                // multiple={false}
                                clearAll={
                                    !filter.hamlet ||
                                    (filter.hamlet as any) === "all" ||
                                    filter.hamlet.length === 0
                                }
                                multiple
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <Grid item xs={12} md={6} lg={3}>
                        <TextField
                            size="small"
                            name="minMonths"
                            label="Number of Defaulter Months"
                            type="number"
                            fullWidth
                            value={filter.minMonths || ""}
                            inputProps={{ min: 0 }}
                            onChange={(e) =>
                                setFilter((f) => ({
                                    ...f,
                                    [e.target.name]:
                                        parseInt(e.target.value) <= 0
                                            ? ""
                                            : parseInt(e.target.value) || "",
                                }))
                            }
                            // InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6} lg={3}>
                        <TextField
                            size="small"
                            name="billingMonth"
                            label="Billing month"
                            type="month"
                            required
                            fullWidth
                            value={billingMonth}
                            onChange={(e) => setBillingMonth(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: currentMonth, min: "2025-10" }}
                            InputProps={{
                                endAdornment: billingMonth ? (
                                    <IconButton
                                        size="small"
                                        onClick={() => setBillingMonth("")}
                                    >
                                        <CloseOutlined />
                                    </IconButton>
                                ) : null,
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6} lg={3}>
                        <TextField
                            size="small"
                            type="date"
                            name="startDate"
                            value={filter.startDate}
                            label="Start Date"
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) =>
                                setFilter({
                                    ...filter,
                                    [e.target.name]: e.target.value,
                                })
                            }
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12} md={6} lg={3}>
                        <TextField
                            size="small"
                            name="endDate"
                            value={filter.endDate}
                            type="date"
                            label="End Date"
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) =>
                                setFilter({
                                    ...filter,
                                    [e.target.name]: e.target.value,
                                })
                            }
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12} md={6} lg={1.4}>
                        <Button
                            fullWidth
                            color="secondary"
                            variant="outlined"
                            sx={{ height: "100%" }}
                            type="submit"
                            startIcon={<FilterAltOutlined />}
                        >
                            Search
                        </Button>
                    </Grid>

                    {/* CLEAR ALL */}
                    <Grid item xs={12} md={6} lg={1.4}>
                        <Button
                            color="error"
                            variant="outlined"
                            startIcon={<FilterAltOffOutlined />}
                            onClick={handleClearAll}
                            sx={{
                                height: "100%",
                                minWidth: theme.spacing(20),
                            }}
                            fullWidth
                        >
                            Clear All
                        </Button>
                    </Grid>
                </Grid>
            </Card>

            {filter.district && filter.district !== "all" && refetchCount ? (
                <TableContainer>
                    {isLoading && <LinearProgress />}

                    <Card variant="outlined" sx={{ px: 1, py: 0.5, mb: 2 }}>
                        <Print
                            componentRef={printRef}
                            setPrintMode={setPrintMode}
                        />

                        <IconButton
                            size="small"
                            onClick={() => setRefetchCount((f) => f + 1)}
                        >
                            <Refresh fontSize="small" />
                        </IconButton>

                        <TableToExcel
                            targetRef={printRef}
                            fileName="Recovery Plan"
                            sheetName="Sheet 1"
                        />
                    </Card>

                    <Box ref={printRef} sx={{ m: printMode ? 2 : 0 }}>
                        <Table size="small" padding="checkbox" stickyHeader>
                            <TableHead>
                                {printMode && (
                                    <TableRow>
                                        <TableCell
                                            colSpan={consumerTypes.length + 4}
                                            sx={{
                                                fontWeight: 900,
                                                textAlign: "center",
                                                bgcolor: "common.white",
                                            }}
                                        >
                                            <Box py={2}>
                                                <Header name="Recovery Plan" />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}

                                <TableRow>
                                    <TableCell sx={{ fontWeight: 900 }}>
                                        Village
                                    </TableCell>

                                    <TableCell sx={{ fontWeight: 900 }}>
                                        Hamlet
                                    </TableCell>

                                    {consumerTypes.map((consumerType) => (
                                        <TableCell
                                            sx={{ fontWeight: 900 }}
                                            key={consumerType}
                                        >
                                            <Typography
                                                noWrap
                                                variant="body2"
                                                textAlign="center"
                                                fontWeight="inherit"
                                            >
                                                {consumerType}
                                            </Typography>
                                        </TableCell>
                                    ))}

                                    <TableCell
                                        sx={{
                                            fontWeight: 900,
                                            textAlign: "center",
                                        }}
                                    >
                                        Total
                                    </TableCell>

                                    <TableCell
                                        sx={{
                                            fontWeight: 900,
                                            textAlign: "center",
                                        }}
                                    >
                                        Defaulters
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {data
                                    // .map((r) => r.hamlet)
                                    // .flat()
                                    .map((row, idx) => (
                                        <TableRow
                                            key={`recovery-plan-row-${idx}`}
                                            sx={{
                                                bgcolor:
                                                    idx % 2 === 0
                                                        ? "inherit"
                                                        : "common.white",

                                                "&:hover": {
                                                    bgcolor: "grey.300",
                                                },
                                            }}
                                        >
                                            <TableCell>
                                                <Typography
                                                    noWrap
                                                    variant="body2"
                                                >
                                                    {row.village}
                                                </Typography>
                                            </TableCell>

                                            <TableCell
                                                sx={{
                                                    // border: "1px solid red",
                                                    px: "0!important",
                                                }}
                                            >
                                                {row.hamlet.map(
                                                    (hamlet, hamletIdx) => (
                                                        <Box key={hamlet.id}>
                                                            <Typography
                                                                noWrap
                                                                variant="body2"
                                                                sx={(
                                                                    theme
                                                                ) => ({
                                                                    px: 2,
                                                                    borderBottom:
                                                                        hamletIdx ===
                                                                        row
                                                                            .hamlet
                                                                            .length -
                                                                            1
                                                                            ? undefined
                                                                            : `1px solid ${theme.palette.divider}`,
                                                                })}
                                                            >
                                                                {hamlet.name}
                                                            </Typography>
                                                        </Box>
                                                    )
                                                )}
                                            </TableCell>

                                            {consumerTypes.map(
                                                (consumerType, ctIdx) => (
                                                    <TableCell
                                                        key={`consumer-type-${ctIdx}`}
                                                        sx={{
                                                            px: "0!important",
                                                        }}
                                                    >
                                                        {row.hamlet.map(
                                                            (
                                                                hamlet,
                                                                hamletIdx
                                                            ) => (
                                                                <Box
                                                                    key={`consumer-type-hamlet-${hamlet.id}`}
                                                                    sx={(
                                                                        theme
                                                                    ) => ({
                                                                        borderBottom:
                                                                            hamletIdx ===
                                                                            row
                                                                                .hamlet
                                                                                .length -
                                                                                1
                                                                                ? undefined
                                                                                : `1px solid ${theme.palette.divider}`,
                                                                    })}
                                                                >
                                                                    <Typography
                                                                        noWrap
                                                                        variant="body2"
                                                                        textAlign="center"
                                                                    >
                                                                        {hamlet
                                                                            .countOfConsumers[
                                                                            consumerType
                                                                        ] ?? 0}
                                                                    </Typography>
                                                                </Box>
                                                            )
                                                        )}
                                                    </TableCell>
                                                )
                                            )}

                                            <TableCell
                                                sx={{ px: "0!important" }}
                                            >
                                                {row.hamlet.map(
                                                    (hamlet, hamletIdx) => (
                                                        <Box
                                                            key={`total-consumers-hamlet-${hamlet.id}`}
                                                            sx={(theme) => ({
                                                                borderBottom:
                                                                    hamletIdx ===
                                                                    row.hamlet
                                                                        .length -
                                                                        1
                                                                        ? undefined
                                                                        : `1px solid ${theme.palette.divider}`,
                                                            })}
                                                        >
                                                            <Typography
                                                                noWrap
                                                                variant="body2"
                                                                textAlign="center"
                                                            >
                                                                {consumerTypes.reduce(
                                                                    (
                                                                        prev,
                                                                        curr
                                                                    ) =>
                                                                        prev +
                                                                        (hamlet
                                                                            .countOfConsumers[
                                                                            curr
                                                                        ] || 0),
                                                                    0
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    )
                                                )}
                                            </TableCell>

                                            <TableCell
                                                sx={{
                                                    px: "0!important",
                                                }}
                                            >
                                                {row.hamlet.map(
                                                    (hamlet, hamletIdx) => (
                                                        <Box
                                                            key={`defaulters-hamlet-${hamlet.id}`}
                                                            sx={(theme) => ({
                                                                borderBottom:
                                                                    hamletIdx ===
                                                                    row.hamlet
                                                                        .length -
                                                                        1
                                                                        ? undefined
                                                                        : `1px solid ${theme.palette.divider}`,
                                                            })}
                                                        >
                                                            <Typography
                                                                noWrap
                                                                variant="body2"
                                                                textAlign="center"
                                                            >
                                                                {
                                                                    hamlet.countOfDefaulters
                                                                }
                                                            </Typography>
                                                        </Box>
                                                    )
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>

                            <TableRow sx={{ bgcolor: "grey.300" }}>
                                <TableCell colSpan={2}>
                                    <Typography fontWeight={900}>
                                        Grand Total
                                    </Typography>
                                </TableCell>

                                {consumerTypes.map((ct) => (
                                    <TableCell key={ct} align="center">
                                        <Typography fontWeight={900}>
                                            {grandTotals[ct]}
                                        </Typography>
                                    </TableCell>
                                ))}

                                <TableCell align="center">
                                    <Typography fontWeight={900}>
                                        {totalConsumers}
                                    </Typography>
                                </TableCell>

                                <TableCell align="center">
                                    <Typography fontWeight={900}>
                                        {totalDefaulters}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </Table>
                    </Box>
                </TableContainer>
            ) : (
                <Box>
                    <Alert severity="info">
                        Please select a district to view the recovery plan.
                    </Alert>
                </Box>
            )}
        </div>
    );
}
